import {
  AfterViewInit,
  Component,
  ElementRef,
  Inject,
  Injector,
  OnDestroy,
  OnInit,
  ViewChild
} from '@angular/core';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogModule,
  MatDialogRef
} from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { TranslateModule } from '@ngx-translate/core';
import { MatRadioModule } from '@angular/material/radio';
import { RouterModule } from '@angular/router';
import { assets } from '../../../core/constants/assets.const';
import { Subject, take, takeUntil } from 'rxjs';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators
} from '@angular/forms';
import { BaseClass } from '../../../shared/baseClass/baseClass';
import { SignUpBody, User, UserSignUp } from '../../../models/auth.model';
import { nameValidator } from '../../../core/helpers/validators.helpers';
import {
  emailErrors,
  nameErrors
} from '../../../core/constants/commonErrors.const';
import { NgxMatIntlTelInputComponent } from 'ngx-mat-intl-tel-input';
import { countryCode } from '../../../models/countryCode.model';
import { CountryCodeConst } from '../../../core/constants/countryCode.const';
import { AuthService } from '../../../services/auth.service';
import { OtpType, SignUpType } from '../../../core/constants/keys.constant';
import { VerificationComponent } from '../verification/verification.component';
import { responseStatus } from '../../../core/constants/responseStatus.const';
import { ImageUploadService } from '../../../services/upload-image.service';
import { GooglePlacesService } from '../../../services/google-place.service';
import { addressChange } from '../../../core/helpers/addressChange.helper';
import { profileSetupStatus } from '../../../core/constants/booking.const';

@Component({
  selector: 'xplora-website-profile-setup',
  standalone: true,
  imports: [
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatDialogModule,
    TranslateModule,
    MatRadioModule,
    RouterModule,
    ReactiveFormsModule,
    NgxMatIntlTelInputComponent
  ],
  templateUrl: './profile-setup.component.html',
  styleUrl: './profile-setup.component.scss'
})
export class ProfileSetupComponent
  extends BaseClass
  implements AfterViewInit, OnDestroy, OnInit {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  @ViewChild('phone') phoneField!: ElementRef | any;

  protected submitted = false;
  private destroy: Subject<boolean> = new Subject<boolean>();
  protected profileFormGroup: FormGroup;
  protected signUpType = SignUpType;
  protected assetsConst = assets;
  protected nameErrors = nameErrors;
  protected emailErrors = emailErrors;
  protected countryCodeObject: countryCode = CountryCodeConst;
  protected phoneVerified = false;
  protected emailVerified = false;
  protected profileSetupStatus = profileSetupStatus;

  constructor(
    injector: Injector,
    private _authService: AuthService,
    private dialogRef: MatDialogRef<ProfileSetupComponent>,
    private dialog: MatDialog,
    private _uploadImageService: ImageUploadService,
    private _googlePlaces: GooglePlacesService,
    @Inject(MAT_DIALOG_DATA) public data: SignUpBody
  ) {
    super(injector);

    this.profileFormGroup = this.formBuilder.group({
      image: [''],
      firstName: ['', nameValidator()],
      email: ['', [ Validators.required, Validators.email]],
      phoneNo: ['', ],
      dialCode: [''],
      // address: this.formBuilder.group({
      //   address: ['', [Validators.required]],
      //   state: ['', [Validators.required]],
      //   city: ['', [Validators.required]],
      //   country: ['', [Validators.required]],
      //   latitude: new FormControl(),
      //   longitude: new FormControl()
      // })
      address: this.formBuilder.group({
        address: [''],
        state: [''],
        city: [''],
        country: [''],
        latitude: new FormControl(),
        longitude: new FormControl()
      })
    });
  }

  ngOnInit(): void {
    this.profileFormGroup
      .get('address')?.get('address')
      ?.valueChanges.subscribe((value) => {
        if (!value) {
          this.profileFormGroup.patchValue({
            address: {
              state: '',
              country: '',
              city: '',
              pinCode: ''
            }
          });
        }
      });
  }

  ngAfterViewInit(): void {
    if (this.data && this.data.data) {
      if (this.data.signUpType === SignUpType.Email) {
        this.profileFormGroup.patchValue({ firstName: this.data.data.firstName });
        this.profileFormGroup.patchValue({ email: this.data.data.email });
        this.profileFormGroup.patchValue({address: this.data.data.address });
        if (this.data.data.dialCode) {
          this.mapCountryFlagToMatInput(this.data.data.dialCode);
        }
        if (this.data.data.phoneNo) {
          this.phoneVerified = true;
          this.profileFormGroup.patchValue({
            phoneNo: `${this.data.data.dialCode}${this.data.data.phoneNo}`
          });
        }

        this.profileFormGroup.get('email')?.disable();
        this.profileFormGroup.get('email')?.updateValueAndValidity();
        this.emailVerified = true;
      } else {
        if (this.data.data.dialCode) {
          this.mapCountryFlagToMatInput(this.data.data.dialCode);
          this.profileFormGroup.patchValue({
            phoneNo: `${this.data.data.dialCode}${this.data.data.phoneNo}`
          });
        }
        this.profileFormGroup.get('phoneNo')?.disable();
        this.profileFormGroup.get('dialCode')?.disable();
        this.profileFormGroup.get('phoneNo')?.updateValueAndValidity();
        this.profileFormGroup.get('dialCode')?.updateValueAndValidity();
        this.phoneVerified = true;
      }
    }
  }

  mapCountryFlagToMatInput(country_code: string | number): void {
    country_code =
      typeof country_code === 'number' ? country_code.toString() : country_code;
    let countryCode: string | string[] = country_code.split('');
    countryCode.shift();
    countryCode = countryCode.join('');
    const countryCodeObject = this.phoneField['allCountries'].find(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (el: any) => el['dialCode'] === countryCode
    );

    this.phoneField['selectedCountry'] = countryCodeObject;
    this.countryCodeObject = countryCodeObject;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  countryChangedEvent(event: any): void {
    this.countryCodeObject = event;
  }

  verifyField(fieldType: number): void {
    if (fieldType === SignUpType.Email) {
      const payload: UserSignUp = {
        email: this.profileFormGroup.get('email')?.value
      };

      if (this.profileFormGroup.get('email')?.valid) {
        this.handleFieldVerification(payload, fieldType);
      }
    } else {
      if (this.profileFormGroup.get('phoneNo')?.valid) {
        const modifiedValue = this.patchValues();
        const payload: UserSignUp = {
          phoneNo: modifiedValue.phoneNo,
          dialCode: modifiedValue.dialCode
        };
        this.handleFieldVerification(payload, fieldType);
      }
    }
  }

  handleFieldVerification(payload: UserSignUp, type: number): void {
    this.spinnerService.addToLoader('verifyField');
    this._authService
      .validateAccount(payload)
      .pipe(takeUntil(this.destroy))
      .subscribe({
        next: () => {
          this.spinnerService.removeFromLoader('verifyField');
          const dialogRef = this.dialog.open(VerificationComponent, {
            panelClass: ['modal', 'auth_modal'],
            data: {
              profileSetup: true,
              data: this.profileFormGroup.value,
              signUpType: type,
              otpType: OtpType.Verification
            }
          });

          dialogRef
            .afterClosed()
            .pipe(takeUntil(this.destroy))
            .subscribe({
              next: (res: number) => {
                if (res === responseStatus.SUCCESS) {
                  if (type === SignUpType.Email) {
                    this.profileFormGroup.get('email')?.disable();
                    this.emailVerified = true;
                  } else {
                    this.profileFormGroup.get('phoneNo')?.disable();
                    this.profileFormGroup.get('dialCode')?.disable();
                    this.phoneVerified = true;
                  }
                }
              }
            });
        },
        error: () => {
          this.spinnerService.removeFromLoader('verifyField');
        }
      });
  }

  onFileChange(event: Event): void {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    this._uploadImageService.onFileChange(event).then((res: any) => {
      this.profileFormGroup.patchValue({
        image: res['data']['image']
      });
    });
  }

  onSubmit(): void {
    this.submitted = true;
    this.trimFormFields();
    if (
      this.profileFormGroup.valid 
      // this.phoneVerified &&
      // this.emailVerified
    ) {
      const modifiedValue = this.patchValues();

      modifiedValue.profileStepsCompleted = this.profileSetupStatus.profileSetup;
      this.spinnerService.addToLoader('updateProfile');
      this._authService
        .updateProfile(modifiedValue)
        .pipe(takeUntil(this.destroy))
        .subscribe({
          next: (res: User) => {
            this.spinnerService.removeFromLoader('updateProfile');
            this._authService.profileDetails.next(res);
            this.dialogRef.close();
          },
          error: () => {
            this.spinnerService.removeFromLoader('updateProfile');
          }
        });
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  patchValues(): any {
    // eslint-disable-next-line prefer-const, @typescript-eslint/no-explicit-any
    let value: any;
    const patchValueToCountryCode = (): void => {
      let tempCountryCode = this.profileFormGroup.get('dialCode')?.value;
      if (tempCountryCode) {
        tempCountryCode = tempCountryCode.split('');
        tempCountryCode.shift();
        tempCountryCode = tempCountryCode.join('');
      }
      if (tempCountryCode !== this.countryCodeObject.dialCode) {
        this.profileFormGroup.patchValue({
          dialCode: `+${this.countryCodeObject.dialCode}`
        });
      }
    };
    const patchValueToPhoneField = (): void => {
      let phone = this.profileFormGroup.get('phoneNo')?.value;
      if (
        phone &&
        phone.includes(this.profileFormGroup.get('dialCode')?.value)
      ) {
        phone = phone.split(this.profileFormGroup.get('dialCode')?.value);
        if (value.phoneNo !== undefined) {
          value.phoneNo = phone[1].trim();
        }
      }
    };
    patchValueToCountryCode();
    value = this.profileFormGroup['value'];
    patchValueToPhoneField();
    return value;
  }

  trimFormFields(): void {
    this.profileFormGroup.patchValue({
      firstName: this.profileFormGroup.get('firstName')?.value.trim(),
      address: {
        address: this.profileFormGroup
          .get('address')
          ?.get('address')
          ?.value.trim(),
        city: this.profileFormGroup.get('address')?.get('city')?.value.trim(),
        state: this.profileFormGroup.get('address')?.get('state')?.value.trim(),
        country: this.profileFormGroup.get('address')?.get('country')?.value.trim()
      }
    });

    if (this.profileFormGroup.get('email')?.enabled) {
      this.profileFormGroup.patchValue({
        email: this.profileFormGroup.get('email')?.value.trim()
      });
    }
  }

  protected addressChange(place: any): void {
    this._googlePlaces.initAutocomplete(place);
    this._googlePlaces
      .getPlaceObservable()
      .pipe(take(1))
      .subscribe({
        next: location => {
          const addressDetails = addressChange(location);
          this.profileFormGroup.patchValue({
            address: {
              address: addressDetails.address,
              city: addressDetails.city,
              state: addressDetails.state,
              latitude: addressDetails.lat,
              longitude: addressDetails.lng,
              country: addressDetails.country
            }
          });
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy.complete();
    this.destroy.unsubscribe();
  }
}
